"use client";

import Image from "next/image";
import Link from "next/link";
import { useAppRouter } from "~/app/_components/useAppRouter";
import { useSession } from "next-auth/react";

import { Button } from "~/app/_components/Button";
import { maxQuantityForCartLine, useCart } from "~/app/_components/CartContext";

export function CartModal() {
  const router = useAppRouter();
  const { data: session, status } = useSession();
  const {
    items,
    isOpen,
    closeCart,
    updateQuantity,
    removeItem,
    clearCart,
    totalPrice,
    syncing,
  } = useCart();

  function handleProceedToCheckout() {
    closeCart();
    if (status === "authenticated" && session?.user) {
      router.push("/checkout");
      return;
    }
    if (status === "unauthenticated") {
      router.push(
        `/signup?callbackUrl=${encodeURIComponent("/checkout")}`,
      );
    }
  }

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm"
        aria-hidden
        onClick={closeCart}
      />
      <div
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-stone-200 bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label="My Cart"
      >
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex items-center justify-between border-b border-stone-200 px-4 py-4">
            <h2 className="text-lg font-semibold text-stone-900">My Cart</h2>
            <button
              type="button"
              onClick={closeCart}
              className="cursor-pointer rounded p-1 text-stone-500 transition hover:bg-stone-100 hover:text-stone-900"
              aria-label="Close cart"
            >
              <span aria-hidden>&times;</span>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {items.length === 0 ? (
              <p className="py-8 text-center text-stone-500">
                Your cart is empty.
              </p>
            ) : (
              <ul className="space-y-4">
                {items.map((item) => {
                  const cap = maxQuantityForCartLine(item.maxQuantity);
                  return (
                  <li
                    key={item.key}
                    className="flex gap-3 border-b border-stone-100 pb-4 last:border-0"
                  >
                    <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-stone-100">
                      <Image
                        src={item.image}
                        alt=""
                        fill
                        className="object-cover"
                        sizes="64px"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-stone-900">
                        {item.name}
                      </p>
                      <div className="mt-1 flex items-center gap-2">
                        <div className="flex items-center gap-0.5 rounded border border-stone-200">
                          <button
                            type="button"
                            onClick={() =>
                              updateQuantity(item.key, item.quantity - 1)
                            }
                            className="flex h-7 w-7 items-center justify-center text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>
                          <span className="min-w-10 px-1 text-center text-sm tabular-nums">
                            {item.quantity}
                            {cap != null ? (
                              <span className="text-stone-400">/{cap}</span>
                            ) : null}
                          </span>
                          <button
                            type="button"
                            disabled={cap != null && item.quantity >= cap}
                            onClick={() =>
                              updateQuantity(item.key, item.quantity + 1)
                            }
                            title={
                              cap != null && item.quantity >= cap
                                ? "Maximum quantity for this item"
                                : undefined
                            }
                            className="flex h-7 w-7 items-center justify-center text-stone-600 hover:bg-stone-100 hover:text-stone-900 disabled:cursor-not-allowed disabled:opacity-40"
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(item.key)}
                          className="text-sm text-amber-700 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <p className="shrink-0 text-sm font-medium text-stone-700">
                      {item.price}
                    </p>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
          {items.length > 0 && (
            <>
              <div className="border-t border-stone-200 px-4 py-3">
                <p className="text-base font-semibold text-stone-900">
                  Total: {totalPrice}
                </p>
              </div>
              <div className="flex flex-col gap-2 border-t border-stone-200 p-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                <button
                  type="button"
                  onClick={closeCart}
                  className="w-full rounded-lg border border-stone-300 bg-white px-4 py-2.5 font-medium text-stone-700 transition hover:bg-stone-50 sm:w-auto"
                >
                  Back
                </button>
                <div className="flex flex-col gap-2 sm:ml-auto sm:flex-row sm:items-center sm:gap-3">
                  <Button
                    type="button"
                    variant="dark"
                    loading={syncing}
                    onClick={() => clearCart()}
                    className="w-full rounded-lg px-4 py-2.5 sm:w-auto"
                  >
                    Clear All
                  </Button>
                  <Button
                    type="button"
                    loading={status === "loading"}
                    onClick={handleProceedToCheckout}
                    className="w-full rounded-lg px-4 py-2.5 text-center sm:w-auto"
                  >
                    Proceed to Checkout
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
